import React from 'react';
import styled from 'styled-components';
import withPageEntry from '../../withPageEntry';
import * as AppActions from '../../AppActions';
import * as JStorage from '../../JStorage';
import * as Ant from 'antd';
import {Link, navigate} from 'gatsby';
import qs from 'query-string';
import Theme from '../../Theme';
import useDocuments from '../../Hooks/useDocuments';
import FilterSection from './FilterSection';
import Breadcrumb from '../../Components/Breadcrumb';
import * as CatUtil from '../../Utils/CatUtil';
import ShareSection from '../../Components/ShareSection';
import {useOutlet} from 'reconnect.js';
import useArea from '../../Hooks/useArea';

function ListPage(props) {
  const {pageContext} = props;
  const {collection, categories_key, cat_field = 'category'} = pageContext;
  const {id, cat} = qs.parse(props.location.search) || {};
  const [record, setRecord] = React.useState();
  const [user] = useOutlet('user');
  const today = new Date().toLocaleDateString('sv');
  const categories = CatUtil.getCategories(categories_key);
  const category = categories.find((c) => c.name === cat);
  const {isDoctorByPathname} = useArea();
  const isDoctor = isDoctorByPathname(props.location.pathname);

  const {records, refresh, filters, setFilters, total} = useDocuments({
    defaultFilters: {
      start_date: {$lte: today},
      $or: [{end_date: {$gte: today}}, {end_date: null}],
      ...(isDoctor ? {doctor_area: true} : {public_area: true}),
      [cat_field]: cat,
    },
    collection,
    projection: {content: 0, html: 0},
    sorting: ['-top', '-start_date'],
  });

  React.useEffect(() => {
    async function fetchData() {
      AppActions.setLoading(true);
      try {
        setRecord(
          await JStorage.fetchOneDocument(
            collection,
            {
              id: id || '-',
              start_date: {$lte: today},
              $or: [{end_date: {$gte: today}}, {end_date: null}],
              ...(isDoctor ? {doctor_area: true} : {public_area: true}),
              ...(!user?.token && {member_only: {$ne: true}}),
            },
            null,
          ),
        );

        if (collection === 'Article_Default') {
          await AppActions.tdaArticleView(id); // count views
        }
      } catch (ex) {
        console.warn('REV_DBG', ex);
      }
      AppActions.setLoading(false);
    }

    if (id) {
      fetchData();
    }
  }, [id, isDoctor, user?.token]);

  React.useEffect(() => {
    if (!id) {
      setRecord(null);
      refresh();
    }
  }, [id, refresh]);

  React.useEffect(() => {
    // if condition for prevent extra fetch at first
    if (filters?.[cat_field] !== cat) {
      setFilters((prev) => ({
        ...prev,
        [cat_field]: cat,
        offset: 0,
        sub_category: undefined,
      }));
    }
  }, [cat, filters?.[cat_field]]);

  return (
    <Wrapper>
      {id && record ? (
        <>
          <div className="tda-header">
            <h2>{record?.category}</h2>
            <Breadcrumb third={record?.category} />
          </div>
          <div className="tda-detail-title">
            <div className="category">{record.category}</div>
            <h1>{record.title}</h1>

            <div className="date">發布日期：{record.start_date}</div>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: record.raw_html?.enabled
                ? record.raw_html.html
                : record.richtext?.html
                ? record.richtext?.html
                : record.html,
            }}
          />
          <div className="attachment-container">
            {(record.attachments || []).map((file, idx) => (
              <a
                key={idx}
                className="attachment"
                target="_blank"
                href={file.url}
                rel="noreferrer">
                {file.description}
              </a>
            ))}
          </div>
          <ShareSection
            url={
              props.location.origin +
              props.location.pathname +
              props.location.search
            }
          />
          <div style={{textAlign: 'center', marginTop: 20}}>
            <Ant.Button
              type="primary"
              onClick={() =>
                AppActions.navigate(
                  props.location.pathname +
                    qs.exclude(props.location.search, ['id']),
                )
              }>
              返回列表
            </Ant.Button>
          </div>
        </>
      ) : id && !record ? (
        <div style={{marginTop: 20}}>
          <h2>無法顯示內容</h2>
          <div>內容可能未開放、已關閉、權限不足</div>
        </div>
      ) : (
        <React.Fragment>
          <div className="tda-header">
            <h2>{category?.name}</h2>
            <Breadcrumb third={category?.name} />
          </div>

          {!user && (
            <div style={{marginBottom: 12}}>※ 部份文章需要會員權限才可觀看</div>
          )}

          <FilterSection
            collection={collection}
            categories_key={categories_key}
            filters={filters}
            setFilters={setFilters}
          />
          <Ant.Table
            className={
              collection === 'Article_Default' ? 'table-color-secondary' : ''
            }
            dataSource={records}
            columns={[
              ...(collection === 'health_education'
                ? [
                    {
                      title: '類別',
                      key: 'sub_category',
                      dataIndex: 'sub_category',
                    },
                  ]
                : [{title: '類別', key: 'category', dataIndex: 'category'}]),
              {
                title: '標題',
                key: 'title',
                render: (item) => (
                  <React.Fragment>
                    {item.member_only && !user ? (
                      <span>{item.title}</span>
                    ) : (
                      <Link to={props.location.pathname + `?id=${item.id}`}>
                        {item.title}
                      </Link>
                    )}
                    {item.member_only && !user && (
                      <Ant.Tag style={{marginLeft: 8}}>請登入會員</Ant.Tag>
                    )}
                  </React.Fragment>
                ),
              },
              {
                title: '發布日期',
                key: 'start_date',
                dataIndex: 'start_date',
              },
              ...(collection === 'Article_Default'
                ? [{title: '點擊', key: 'view_count', dataIndex: 'view_count'}]
                : []),
            ]}
            rowKey={'id'}
            pagination={{
              total,
              pageSize: filters.limit,
              current: filters.offset / filters.limit + 1,
            }}
            onChange={({pageSize, current}) => {
              setFilters((prev) => ({
                ...prev,
                offset: (current - 1) / prev.limit,
              }));
            }}
          />
        </React.Fragment>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  h1.title {
    text-align: center;
    padding-bottom: 15px;
    border-bottom: 4px solid ${Theme.colors.main};
    margin-bottom: 25px;
  }

  .attachment-container {
    margin: 20px 0;
  }
  .attachment {
    border-radius: 5px;
    border: solid 1px #bbb;
    padding: 8px 12px;
    margin-right: 10px;

    color: #555;
  }

  & .ant-table-thead {
    & > tr > th {
      color: var(--primaryColor);
      padding: 4px 16px;
      background-color: var(--primaryLightColor);
      border-bottom: solid 2px var(--primaryThBorderColor);
    }
  }
  & .table-color-secondary .ant-table-thead {
    & > tr > th {
      color: var(--secondaryColor);
      padding: 4px 16px;
      background-color: var(--secondaryLightColor);
      border-bottom: solid 2px var(--secondaryThBorderColor);
    }
  }

  & .table-color-secondary .ant-table-thead {
    & > tr > th {
      color: var(--secondaryColor);
      padding: 4px 16px;
      background-color: var(--secondaryLightColor);
      border-bottom: solid 2px var(--secondaryThBorderColor);
    }
  }
`;

export default withPageEntry(ListPage);
